/**
 * The FCC Portal app bar across the very top: "FCC Portal", this app's name, the app
 * switcher (the waffle) and the account disc — the same bar every FCC app shows.
 *
 * The element, <fcc-app-bar>, belongs to the portal (fcc-portal, src/appbar/) and is
 * loaded from it at run time: `${VITE_PORTAL_URL}/app-bar.js`. It renders in its own
 * Shadow DOM, so nothing here styles it and none of its CSS reaches this app.
 *
 * VITE_PORTAL_URL unset: renders nothing, loads nothing — the app is exactly as it was.
 * If the script fails to load the element simply stays empty; nothing here depends on it.
 *
 * Roles: for now the bar gets THIS app's roles, from the ID token (Dataset.Read /
 * Dataset.ReadAll), not the portal's App.* roles — so in production the switcher lists
 * only this app until the portal exposes its Apps.Read scope. See the portal README,
 * "Role source". The switcher is navigation, not access control: it gates nothing here.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { usePreferences } from '@/ui/preferences'
import { PreferenceControls } from './UserMenu'

/** The portal's origin, no trailing slash; '' when the bar is off. */
export const PORTAL_URL = (import.meta.env.VITE_PORTAL_URL ?? '').trim().replace(/\/+$/, '')

/** This app's id in the portal's catalog (fcc-portal, src/appCatalog.ts). */
const APP_ID = 'cost-per-claim'

interface FccAppBarElement extends HTMLElement {
  roles: string[]
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'fcc-app-bar': DetailedHTMLProps<HTMLAttributes<FccAppBarElement>, FccAppBarElement> & {
        current?: string
        'portal-url'?: string
        'user-name'?: string
        'user-email'?: string
        'app-name'?: string
        theme?: 'light' | 'dark' | 'auto'
        'no-signout'?: string
      }
    }
  }
}

/**
 * Whether the bar is actually on screen: 'loading' until /app-bar.js defines the
 * element, 'failed' if the script errors or takes longer than BAR_TIMEOUT_MS.
 *
 * AppShell keeps this app's own UserMenu until 'ready', so a portal that is down or slow
 * never leaves someone without Sign out, theme or density.
 */
type BarState = 'loading' | 'ready' | 'failed'
/** The bar's fixed height (fcc-portal, src/appbar/styles.ts `.bar`). */
const BAR_HEIGHT = 48
const BAR_TIMEOUT_MS = 5000

let barState: BarState = 'loading'
const listeners = new Set<() => void>()
function setBarState(s: BarState): void {
  if (barState === 'ready' || barState === s) return
  barState = s
  listeners.forEach((l) => l())
}

/** Adds the portal's module script once per page, and tracks whether it arrived. */
function loadBar(base: string): void {
  if (document.querySelector('script[data-fcc-app-bar]')) return
  const s = document.createElement('script')
  s.type = 'module'
  s.src = `${base}/app-bar.js`
  s.dataset.fccAppBar = ''
  s.onerror = () => setBarState('failed')
  document.head.append(s)
  void customElements.whenDefined('fcc-app-bar').then(() => setBarState('ready'))
  // A late arrival still wins: 'ready' overrides 'failed' (see setBarState).
  setTimeout(() => {
    if (barState === 'loading') setBarState('failed')
  }, BAR_TIMEOUT_MS)
}

/** True once the portal bar has loaded; false while loading, when it failed, or when off. */
export function usePortalBarReady(): boolean {
  const state = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => barState,
  )
  return PORTAL_URL !== '' && state === 'ready'
}

/** The ID token's `roles` claim; only strings are trusted. */
function rolesOf(claims: unknown): string[] {
  const roles = (claims as { roles?: unknown } | null | undefined)?.roles
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : []
}

export function PortalBar() {
  return PORTAL_URL ? <Bar base={PORTAL_URL} /> : null
}

function Bar({ base }: { base: string }) {
  const { account, signOut } = useAuth()
  const { theme } = usePreferences()
  const ref = useRef<FccAppBarElement>(null)
  const roles = useMemo(() => rolesOf(account?.idTokenClaims), [account])

  useEffect(() => loadBar(base), [base])
  const failed = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => barState === 'failed',
  )

  // A property, not an attribute. Set before the script has loaded, it lands on the
  // un-upgraded element and the bar picks it up when it upgrades.
  useEffect(() => {
    if (ref.current) ref.current.roles = roles
  }, [roles])

  useEffect(() => {
    const bar = ref.current
    if (!bar) return
    const onSignOut = () => void signOut()
    bar.addEventListener('fcc-signout', onSignOut)
    return () => bar.removeEventListener('fcc-signout', onSignOut)
  }, [signOut])

  // The bar is pinned, so whatever else is pinned (the page rail, the phone tab row)
  // starts under it: --shell-top, read by AppShell. Cleared with the bar.
  useEffect(() => {
    if (failed) return
    document.documentElement.style.setProperty('--shell-top', `${BAR_HEIGHT}px`)
    return () => {
      document.documentElement.style.removeProperty('--shell-top')
    }
  }, [failed])

  // No portal: no empty 48px band either. UserMenu has stayed in the masthead.
  if (failed) return null

  // Theme and density go into the bar's account menu (slot "settings"): they stay in
  // this app's DOM and CSS, the bar only places them.
  return (
    <fcc-app-bar
      ref={ref}
      current={APP_ID}
      app-name="Cost per Claim"
      portal-url={base}
      user-name={account ? account.name?.trim() || account.username : undefined}
      user-email={account?.username}
      theme={theme === 'system' ? 'auto' : theme}
      // Pinned like any app shell's global bar; above the rail and the tab row.
      style={{ position: 'sticky', top: 0, zIndex: 40 }}
    >
      <div slot="settings" style={{ display: 'grid', gap: 12 }}>
        <PreferenceControls />
      </div>
    </fcc-app-bar>
  )
}
